import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2, Users } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import resourcePlannerApi, { type PlanningContractPreview } from '@/services/resource-planner'
import type { StoreOrderDetail } from '@/types'

type OrderPlanningAllocationPanelProps = {
  order: StoreOrderDetail
}

const toPkValue = (value: string | number | null | undefined): number | null => {
  if (typeof value === 'number') {
    return Number.isInteger(value) && value >= 0 ? value : null
  }
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    return Number(value)
  }
  return null
}

export function OrderPlanningAllocationPanel({ order }: OrderPlanningAllocationPanelProps) {
  const normalizedOrderId = useMemo(() => toPkValue(order.raw_id), [order.raw_id])
  const contractQuery = useQuery({
    queryKey: ['planner-order-contract-allocation', normalizedOrderId ?? null, order.reference_id],
    queryFn: () => resourcePlannerApi.buildContractForOrder(
      normalizedOrderId != null ? { order_id: normalizedOrderId } : { reference_id: order.reference_id },
    ),
    staleTime: 60_000,
  })

  const contract = contractQuery.data as PlanningContractPreview | undefined
  const requirements = contract?.resource_requirements ?? []
  const humanCapabilities = (contract?.resource_capabilities ?? []).filter((entry: unknown) => {
    const record = entry as Record<string, unknown>
    return String(record.type ?? '').toLowerCase() === 'human'
  })

  if (contractQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading allocation requirements...
      </div>
    )
  }

  if (requirements.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No planner resource requirements matched this booking yet.
      </p>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">Scope: {contract?.job_scope ?? 'single_job'}</Badge>
        <Badge variant="secondary">{requirements.length} role{requirements.length === 1 ? '' : 's'}</Badge>
      </div>
      <div className="space-y-2">
        {requirements.map((requirement: NonNullable<PlanningContractPreview['resource_requirements']>[number]) => {
          const capability = humanCapabilities.find((entry: unknown) => {
            const record = entry as Record<string, unknown>
            return record.role === requirement.role
          }) as Record<string, unknown> | undefined
          const metadata = capability?.metadata as Record<string, unknown> | undefined
          const sources = Array.isArray(metadata?.sources) ? metadata.sources : []
          const label = String(metadata?.label ?? requirement.role ?? 'Resource')

          return (
            <div key={String(requirement.role)} className="rounded-lg border border-border/70 bg-background p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-sky-700" />
                  <span className="font-medium">{label}</span>
                </div>
                <span className="text-muted-foreground">
                  min {requirement.min ?? 0} • preferred {requirement.preferred ?? requirement.min ?? 0} • max {requirement.max ?? requirement.min ?? 0}
                </span>
              </div>
              {sources.length > 0 ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  Sources: {sources.map((source) => String((source as Record<string, unknown>).source ?? 'link')).join(', ')}
                </p>
              ) : null}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default OrderPlanningAllocationPanel
